import { useQuery, useQueryClient } from 'react-query';
import type { WeeklyCopyFormat } from '../../../../lib/shared/types';
import { useAuth } from '../../contexts/AuthContext';
import { weeklyCopyPreferenceApi } from '../../lib/api';

export const isWeeklyCopyFormat = (value: unknown): value is WeeklyCopyFormat =>
  value === 'rtf' || value === 'markdown' || value === 'whatsapp';

export function useWeeklyCopyPreference() {
  const { user } = useAuth();
  const client = useQueryClient();
  const key = ['weekly-copy-preference', user?.id];
  const query = useQuery<WeeklyCopyFormat>(
    key,
    async () => {
      const result = await weeklyCopyPreferenceApi.get();
      if (
        result.error ||
        !result.data ||
        (result.data.format !== null && !isWeeklyCopyFormat(result.data.format))
      ) {
        throw new Error('Weekly copy preference unavailable');
      }
      return result.data.format ?? 'whatsapp';
    },
    { enabled: !!user?.id, retry: false }
  );
  const save = async (format: WeeklyCopyFormat) => {
    if (!user?.id || !isWeeklyCopyFormat(format)) return false;
    try {
      const result = await weeklyCopyPreferenceApi.save({ format });
      if (result.error || result.data?.format !== format) return false;
      client.setQueryData(key, format);
      return true;
    } catch {
      return false;
    }
  };
  return {
    format: query.isError ? undefined : query.data,
    loading: query.isLoading,
    error: query.isError,
    retry: () => void query.refetch(),
    save,
  };
}
